import {
  Input,
  Select,
  Button,
  Alert,
  Form as AntForm,
  ColorPicker,
} from "antd";
import { Formik, FieldArray } from "formik";
import { PlusOutlined } from "@ant-design/icons";
import { useState, useRef, useEffect, useContext } from "react";
import { loadChatConfig, saveChatConfig } from "./utils";
import ChatConfigContext from "./ChatConfigContext";

const ImageUpload = ({ value, onChange }) => {
  const uploadButton = useRef(null);

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleUpload = async (file) => {
    const base64 = await getBase64(file);
    const fileName = file.name;
    const fileType = file.type;
    const response = await fetch(
      `https://pfx1jlc6k6.execute-api.eu-west-3.amazonaws.com/prod/get-presigned-upload-url?file_name=${encodeURIComponent(
        fileName
      )}&file_type=${encodeURIComponent(fileType)}`
    );
    const data = await response.json();
    const uploadUrl = data.upload_url;
    const finalUrl = data.get_url;

    // Convert base64 to blob before uploading
    const base64Response = await fetch(base64);
    const blob = await base64Response.blob();

    await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": fileType,
      },
      body: blob,
    });

    onChange({ uid: file.uid, name: fileName, status: "done", url: finalUrl });
  };

  return (
    <div className="avatar-uploader">
      <input
        type="file"
        name="avatar"
        style={{ display: "none" }}
        onChange={(event) => handleUpload(event.target.files[0])}
        ref={uploadButton}
      />
      <button
        className="upload-btn cursor-pointer w-20 h-20 bg-slate-100 flex flex-col items-center justify-center rounded-full border-dashed hover:bg-slate-200 overflow-hidden"
        onClick={() => uploadButton.current.click()}
      >
        {value ? (
          <img
            src={value}
            alt="avatar"
            className="w-full h-full object-cover"
          />
        ) : (
          <PlusOutlined style={{ position: "absolute" }} />
        )}
      </button>
    </div>
  );
};

const ChatEditor = ({ brand }) => {
  const [initialValues, setInitialValues] = useState({
    id: brand,
    image: "", // Image uploader
    title: "", // Text input
    aiEngine: "", // Select
    color: "#9767ff", // Color picker
    welcomeMessage: "", // Textarea
    questionsSuggestions: [""], // Array of text inputs
  });
  const [savedData, setSavedData] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isError, setIsError] = useState(false);
  const { setChatConfig } = useContext(ChatConfigContext);

  useEffect(() => {
    setChatConfig(savedData);
  }, [savedData, setChatConfig]);

  useEffect(() => {
    if (!brand) return;
    const load = async () => {
      const data = (await loadChatConfig(brand)) || {};
      if (JSON.stringify(data) === JSON.stringify(savedData)) return;
      setInitialValues({
        ...savedData,
        ...data,
      });
      setSavedData({
        ...savedData,
        ...data,
      });
    };
    load();
  }, [brand, savedData, setInitialValues]);

  const formUpdated = (values) => {
    if (savedData !== values) {
      setIsSaved(false);
      setIsError(false);
    }
  };

  const saveData = async (data) => {
    setIsSubmitting(true);
    setIsSaved(false);
    setIsError(false);
    const response = await saveChatConfig(data);
    if (response !== "ERROR") {
      setSavedData(data);
      setIsSaved(true);
    } else {
      setIsError(true);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="w-10/12 mx-auto">
      <Formik initialValues={initialValues} enableReinitialize>
        {({ values, setFieldValue }) => {
          formUpdated(values);
          return (
            <AntForm layout="vertical">
              <AntForm.Item label="Image:">
                <ImageUpload
                  value={values.image}
                  onChange={(info) => {
                    setFieldValue("image", info ? info.url : "");
                  }}
                />
              </AntForm.Item>
              <AntForm.Item label="Chat Color:">
                <ColorPicker
                  format="hex"
                  value={values.chatColor}
                  position="bottom"
                  onChangeComplete={(color) => {
                    setFieldValue("chatColor", color.toHexString());
                  }}
                />
              </AntForm.Item>
              <AntForm.Item label="Title:">
                <Input
                  name="title"
                  value={values.title}
                  placeholder="Enter title"
                  onChange={(e) => setFieldValue("title", e.target.value)}
                />
              </AntForm.Item>
              <AntForm.Item label="AI Engine:">
                <Select
                  name="aiEngine"
                  value={values.aiEngine}
                  onChange={(value) => setFieldValue("aiEngine", value)}
                  placeholder="Select AI Engine"
                  options={[
                    { value: "allobot-generic", label: "AlloBot-Generic" },
                  ]}
                />
              </AntForm.Item>
              <AntForm.Item label="Welcome Message:">
                <Input.TextArea
                  name="welcomeMessage"
                  value={values.welcomeMessage}
                  placeholder="Enter welcome message"
                  onChange={(e) =>
                    setFieldValue("welcomeMessage", e.target.value)
                  }
                />
              </AntForm.Item>
              <AntForm.Item label="Suggestion de question:">
                <FieldArray
                  name="questionsSuggestions"
                  render={(arrayHelpers) => (
                    <div>
                      {values.questionsSuggestions.map((item, index) => (
                        <div key={index}>
                          <Input
                            value={item}
                            placeholder={`Suggestion #${index + 1}`}
                            onChange={(e) => {
                              const newSuggestions = [
                                ...values.questionsSuggestions,
                              ];
                              newSuggestions[index] = e.target.value;
                              setFieldValue(
                                "questionsSuggestions",
                                newSuggestions
                              );
                            }}
                          />
                          <Button
                            type="danger"
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="dashed"
                        onClick={() => arrayHelpers.push("")}
                      >
                        Add Suggestion
                      </Button>
                    </div>
                  )}
                />
              </AntForm.Item>
              <AntForm.Item>
                <Button
                  type="primary"
                  disabled={
                    JSON.stringify(values) === JSON.stringify(savedData) ||
                    isSubmitting
                  }
                  onClick={() => {
                    saveData(values);
                  }}
                >
                  Save Changes
                </Button>
                {isSaved && (
                  <Alert message="Saved." type="success" showIcon banner />
                )}
                {isError && (
                  <Alert message="Error." type="error" showIcon banner />
                )}
              </AntForm.Item>
            </AntForm>
          );
        }}
      </Formik>
    </div>
  );
};

export default ChatEditor;
